import { useEffect, useCallback, useRef } from 'react';

import './PrivacyPolicy.css';
import { useTranslation } from '../i18n/LanguageContext.jsx';

/**
 * Privacy Policy modal component.
 *
 * Displays the GDPR-compliant privacy policy in a modal overlay.
 * Supports bilingual content (HU/EN) via the LanguageContext.
 * Accessible: role="dialog", aria-modal, focus trap, Escape to close.
 * Auto-focuses close button on open for keyboard accessibility.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 */
function PrivacyPolicy({ isOpen, onClose }) {
  const { t } = useTranslation();
  const closeButtonRef = useRef(null);
  const modalRef = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap: cycle Tab within modal
      if (event.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        const isWithinModal = modalRef.current.contains(document.activeElement);

        if (event.shiftKey) {
          if (document.activeElement === first || !isWithinModal) {
            event.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last || !isWithinModal) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose]
  );

  const handleBackdropClick = useCallback(
    (event) => {
      // Only close if clicking the backdrop itself, not content inside
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Auto-focus close button for keyboard accessibility
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
    } else {
      // Ensure body scroll is restored when modal is closed
      document.body.style.overflow = '';
    }

    // Always clean up on unmount or dependency change
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- modal dialog overlay requires event handlers for a11y
    <div
      className="privacy-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('privacyPolicy.title')}
      onKeyDown={handleKeyDown}
      onClick={handleBackdropClick}
      tabIndex={-1}
    >
      <div className="privacy-modal" ref={modalRef}>
        <div className="privacy-header">
          <h2>{t('privacyPolicy.title')}</h2>
          <button
            className="privacy-close"
            ref={closeButtonRef}
            onClick={onClose}
            aria-label={t('privacyPolicy.close')}
          >
            ×
          </button>
        </div>
        {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- scrollable region needs tabIndex for keyboard access */}
        <div
          className="privacy-content"
          tabIndex={0}
          role="region"
          aria-label={t('privacyPolicy.title')}
        >
          {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
          <p className="privacy-intro">{t('privacyPolicy.intro')}</p>

          <h3>{t('privacyPolicy.dataCollected')}</h3>
          <p>{t('privacyPolicy.dataCollectedText')}</p>

          <h3>{t('privacyPolicy.cookies')}</h3>
          <p>{t('privacyPolicy.cookiesText')}</p>

          <h3>{t('privacyPolicy.purpose')}</h3>
          <p>{t('privacyPolicy.purposeText')}</p>

          <h3>{t('privacyPolicy.rights')}</h3>
          <p>{t('privacyPolicy.rightsText')}</p>

          <h3>{t('privacyPolicy.legalBasis')}</h3>
          <p>{t('privacyPolicy.legalBasisText')}</p>

          <h3>{t('privacyPolicy.contact')}</h3>
          <p>{t('privacyPolicy.contactText')}</p>

          <p className="privacy-updated">{t('privacyPolicy.lastUpdated')}</p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
